import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, X } from "lucide-react";
import FileDropZone from "@/components/FileDropZone";
import { ImageUploadField } from "@/components/ImageUploadField";
import { SOCIAL_PLATFORMS } from "@/lib/socials";

export const ROLE_OPTIONS = ["client","actor","crew","producer","director","writer","investor","distributor","supplier","sponsor","consultant","collaborator","general contact"];

const emptyPerson = {
  name: "", email: "", phone: "", location: "", website: "", rates: "", notes: "",
  role_tags: [], relationship_status: "active",
  profile_mode: "simple",
  pronouns: "", date_of_birth: "", languages: [], currency: "AUD",
  day_rate: null, half_day_rate: null, weekly_rate: null,
  showreel_url: "", portfolio_urls: [], resume_url: "", socials: {},
  height_cm: null, weight_kg: null, hair_colour: "", eye_colour: "", measurements: "", ethnicity: "",
  unions: [], agent_name: "", agent_email: "", agent_phone: "", agent_company: "",
  has_own_transport: false, has_own_equipment: false, has_public_liability: false, insurance_notes: "",
  credits: [], references: [], availability_notes: "", travel_willingness: "local",
  tags: [], skills: [],
};

export const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

export const SocialFields = ({ form, set }) => {
  const socials = form.socials || {};
  const update = (key, value) => {
    const next = { ...socials };
    if (value) next[key] = value; else delete next[key];
    set({ socials: next });
  };
  return (
    <div className="space-y-2 pt-1" data-testid="person-socials">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">Socials</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SOCIAL_PLATFORMS.map(({ key, label, placeholder, icon: Icon }) => (
          <div key={key} className="flex items-center gap-2">
            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input data-testid={`social-${key}`} placeholder={`${label} — ${placeholder}`}
                   value={socials[key] ?? ""} onChange={(e) => update(key, e.target.value)} />
          </div>
        ))}
      </div>
    </div>
  );
};

/** Company attachment — pick existing companies and/or type brand new ones. */
function CompanyPicker({ companies, selectedIds, newNames, onChange }) {
  const [draft, setDraft] = useState("");
  const available = companies.filter((c) => !selectedIds.includes(c.id));

  const addTyped = () => {
    const name = draft.trim();
    if (!name) return;
    const hit = companies.find((c) => c.name.toLowerCase() === name.toLowerCase());
    if (hit) onChange({ selectedIds: [...selectedIds, hit.id], newNames });
    else onChange({ selectedIds, newNames: [...newNames, name] });
    setDraft("");
  };

  return (
    <div className="space-y-2" data-testid="person-company-picker">
      <div className="flex flex-wrap gap-1.5">
        {selectedIds.map((id) => {
          const c = companies.find((x) => x.id === id);
          return (
            <Badge key={id} variant="secondary" className="gap-1" data-testid={`person-company-chip-${id}`}>
              {c?.name || "Company"}
              <button type="button" onClick={() => onChange({ selectedIds: selectedIds.filter((x) => x !== id), newNames })}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
        {newNames.map((name) => (
          <Badge key={name} className="gap-1" data-testid={`person-company-new-${name}`}>
            {name} (new)
            <button type="button" onClick={() => onChange({ selectedIds, newNames: newNames.filter((n) => n !== name) })}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid="person-company-input" value={draft} placeholder="Type a company name…"
               list="contact-company-options"
               onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTyped(); } }} />
        <datalist id="contact-company-options">
          {available.map((c) => <option key={c.id} value={c.name} />)}
        </datalist>
        <Button type="button" variant="outline" data-testid="person-company-add" onClick={addTyped}>Add</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Pick one you already have, or type a new name — we'll create the company with this person as its contact.
      </p>
    </div>
  );
}

export function PersonDialog({ person, companies = [], onSave }) {
  const [form, setForm] = useState(emptyPerson);
  const [companyIds, setCompanyIds] = useState([]);
  const [newCompanies, setNewCompanies] = useState([]);

  useEffect(() => {
    const clean = Object.fromEntries(
      Object.entries(person || {}).map(([k, v]) => [k, v === null ? "" : v])
    );
    setForm({ ...emptyPerson, ...clean });
    setCompanyIds(person?.company_ids || []);
    setNewCompanies([]);
  }, [person]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleRole = (t) => set({ role_tags: form.role_tags?.includes(t) ? form.role_tags.filter((x) => x !== t) : [...(form.role_tags || []), t] });
  const advanced = form.profile_mode === "advanced";

  const addCredit = () => set({ credits: [...(form.credits || []), { title: "", role: "", year: "", type: "", link: "" }] });
  const updateCredit = (i, patch) => set({ credits: form.credits.map((c, ci) => ci === i ? { ...c, ...patch } : c) });
  const removeCredit = (i) => set({ credits: form.credits.filter((_, ci) => ci !== i) });

  const addReference = () => set({ references: [...(form.references || []), { name: "", role: "", contact: "", relationship: "" }] });
  const updateReference = (i, patch) => set({ references: form.references.map((c, ci) => ci === i ? { ...c, ...patch } : c) });
  const removeReference = (i) => set({ references: form.references.filter((_, ci) => ci !== i) });

  const companyBlock = (
    <Field label="Company / companies">
      <CompanyPicker companies={companies} selectedIds={companyIds} newNames={newCompanies}
                     onChange={({ selectedIds, newNames }) => { setCompanyIds(selectedIds); setNewCompanies(newNames); }} />
    </Field>
  );

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3">
          {person?.id ? "Edit contact" : "Add contact"}
          <div className="ml-auto flex items-center gap-2 text-xs font-normal">
            <span className={form.profile_mode === "simple" ? "font-semibold" : "text-muted-foreground"}>Simple</span>
            <Switch data-testid="person-mode-toggle" checked={advanced}
                    onCheckedChange={(v) => set({ profile_mode: v ? "advanced" : "simple" })} />
            <span className={advanced ? "font-semibold text-[hsl(var(--studio-primary))]" : "text-muted-foreground"}>Advanced</span>
          </div>
        </DialogTitle>
      </DialogHeader>

      {!advanced ? (
        <div className="space-y-3">
          <SimpleFields form={form} set={set} toggleRole={toggleRole} />
          {companyBlock}
        </div>
      ) : (
        <Tabs defaultValue="overview" className="mt-2">
          <TabsList className="flex-wrap h-auto" data-testid="advanced-tabs">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="career">Career / Credits</TabsTrigger>
            <TabsTrigger value="rates">Rates &amp; Availability</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">Portfolio &amp; socials</TabsTrigger>
            <TabsTrigger value="representation">Representation</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="space-y-3 pt-3">
            <SimpleFields form={form} set={set} toggleRole={toggleRole} />
            {companyBlock}
            <div className="grid grid-cols-3 gap-3">
              <Field label="Pronouns"><Input value={form.pronouns} onChange={(e) => set({ pronouns: e.target.value })}/></Field>
              <Field label="Date of birth"><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set({ date_of_birth: e.target.value })}/></Field>
              <Field label="Ethnicity"><Input value={form.ethnicity} onChange={(e) => set({ ethnicity: e.target.value })}/></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Languages (comma-sep)"><Input value={(form.languages||[]).join(", ")} onChange={(e) => set({ languages: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/></Field>
              <Field label="Skills (comma-sep)"><Input value={(form.skills||[]).join(", ")} onChange={(e) => set({ skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Height (cm)"><Input type="number" value={form.height_cm ?? ""} onChange={(e) => set({ height_cm: e.target.value ? parseFloat(e.target.value) : null })}/></Field>
              <Field label="Hair colour"><Input value={form.hair_colour} onChange={(e) => set({ hair_colour: e.target.value })}/></Field>
              <Field label="Eye colour"><Input value={form.eye_colour} onChange={(e) => set({ eye_colour: e.target.value })}/></Field>
            </div>
          </TabsContent>
          <TabsContent value="career" className="space-y-3 pt-3">
            <div className="text-xs text-muted-foreground">Credits — films, TV, commercials, theatre, music videos.</div>
            {(form.credits || []).map((c, i) => (
              <div key={`credit-${i}`} className="grid grid-cols-12 gap-2 items-start">
                <Input className="col-span-4" placeholder="Title" value={c.title} onChange={(e) => updateCredit(i, { title: e.target.value })}/>
                <Input className="col-span-3" placeholder="Role" value={c.role} onChange={(e) => updateCredit(i, { role: e.target.value })}/>
                <Input className="col-span-2" placeholder="Type" value={c.type} onChange={(e) => updateCredit(i, { type: e.target.value })}/>
                <Input className="col-span-1" placeholder="Year" value={c.year} onChange={(e) => updateCredit(i, { year: e.target.value })}/>
                <Input className="col-span-2" placeholder="Link" value={c.link} onChange={(e) => updateCredit(i, { link: e.target.value })}/>
                <Button variant="ghost" size="icon" className="col-span-12 md:col-span-1 justify-self-end" onClick={() => removeCredit(i)}><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addCredit} data-testid="add-credit-btn"><Plus className="w-3.5 h-3.5 mr-1"/> Add credit</Button>
          </TabsContent>
          <TabsContent value="rates" className="space-y-3 pt-3">
            <div className="grid grid-cols-4 gap-3">
              <Field label="Currency"><Input value={form.currency} onChange={(e) => set({ currency: e.target.value })}/></Field>
              <Field label="Day rate"><Input type="number" value={form.day_rate ?? ""} onChange={(e) => set({ day_rate: e.target.value ? parseFloat(e.target.value) : null })}/></Field>
              <Field label="Half-day rate"><Input type="number" value={form.half_day_rate ?? ""} onChange={(e) => set({ half_day_rate: e.target.value ? parseFloat(e.target.value) : null })}/></Field>
              <Field label="Weekly rate"><Input type="number" value={form.weekly_rate ?? ""} onChange={(e) => set({ weekly_rate: e.target.value ? parseFloat(e.target.value) : null })}/></Field>
            </div>
            <Field label="Availability notes"><Textarea rows={2} value={form.availability_notes} onChange={(e) => set({ availability_notes: e.target.value })}/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Travel willingness"><Input placeholder="local | regional | national | international" value={form.travel_willingness} onChange={(e) => set({ travel_willingness: e.target.value })}/></Field>
              <Field label="Rates text (free form)"><Input value={form.rates} onChange={(e) => set({ rates: e.target.value })}/></Field>
            </div>
          </TabsContent>
          <TabsContent value="portfolio" className="space-y-3 pt-3">
            <Field label="Showreel URL"><Input data-testid="person-showreel" value={form.showreel_url} onChange={(e) => set({ showreel_url: e.target.value })} placeholder="https://vimeo.com/..."/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Website"><Input data-testid="person-website-portfolio" value={form.website || ""} onChange={(e) => set({ website: e.target.value })} placeholder="https://"/></Field>
              <Field label="Resume / CV URL"><Input data-testid="person-resume" value={form.resume_url || ""} onChange={(e) => set({ resume_url: e.target.value })} placeholder="https://"/></Field>
            </div>
            <Field label="Other portfolio links (comma-sep)"><Textarea rows={2} value={(form.portfolio_urls||[]).join(", ")} onChange={(e) => set({ portfolio_urls: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/></Field>
            <SocialFields form={form} set={set} />
          </TabsContent>
          <TabsContent value="representation" className="space-y-3 pt-3">
            <Field label="Agent name"><Input value={form.agent_name} onChange={(e) => set({ agent_name: e.target.value })}/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Agent email"><Input value={form.agent_email} onChange={(e) => set({ agent_email: e.target.value })}/></Field>
              <Field label="Agent phone"><Input value={form.agent_phone} onChange={(e) => set({ agent_phone: e.target.value })}/></Field>
            </div>
            <Field label="Agent company"><Input value={form.agent_company} onChange={(e) => set({ agent_company: e.target.value })}/></Field>
            <Field label="Unions / memberships (comma-sep)"><Input value={(form.unions||[]).join(", ")} onChange={(e) => set({ unions: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/></Field>
            <div className="text-xs text-muted-foreground pt-2">References</div>
            {(form.references || []).map((r, i) => (
              <div key={`ref-${i}`} className="grid grid-cols-12 gap-2 items-start">
                <Input className="col-span-3" placeholder="Name" value={r.name} onChange={(e) => updateReference(i, { name: e.target.value })}/>
                <Input className="col-span-3" placeholder="Role" value={r.role} onChange={(e) => updateReference(i, { role: e.target.value })}/>
                <Input className="col-span-3" placeholder="Contact" value={r.contact} onChange={(e) => updateReference(i, { contact: e.target.value })}/>
                <Input className="col-span-2" placeholder="Relationship" value={r.relationship} onChange={(e) => updateReference(i, { relationship: e.target.value })}/>
                <Button variant="ghost" size="icon" className="col-span-1 justify-self-end" onClick={() => removeReference(i)}><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addReference}><Plus className="w-3.5 h-3.5 mr-1"/> Add reference</Button>
          </TabsContent>
          <TabsContent value="compliance" className="space-y-3 pt-3">
            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer text-sm">
                <input type="checkbox" checked={!!form.has_own_transport} onChange={(e) => set({ has_own_transport: e.target.checked })}/> Own transport
              </label>
              <label className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer text-sm">
                <input type="checkbox" checked={!!form.has_own_equipment} onChange={(e) => set({ has_own_equipment: e.target.checked })}/> Own equipment
              </label>
              <label className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer text-sm">
                <input type="checkbox" checked={!!form.has_public_liability} onChange={(e) => set({ has_public_liability: e.target.checked })}/> Public liability
              </label>
            </div>
            <Field label="Insurance / compliance notes"><Textarea rows={3} value={form.insurance_notes} onChange={(e) => set({ insurance_notes: e.target.value })}/></Field>
          </TabsContent>
        </Tabs>
      )}

      {person?.id && (
        <div className="pt-3 border-t border-border space-y-1.5">
          <Label className="text-xs">Attachments</Label>
          <FileDropZone context={{ person_id: person.id }} compact data-testid="person-dropzone"
                        label="Drop files (contracts, headshots, references)" />
        </div>
      )}

      <DialogFooter className="pt-4">
        <Button data-testid="person-save" disabled={!form.name?.trim()}
                onClick={() => onSave(form, { companyIds, newCompanies })}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function SimpleFields({ form, set, toggleRole }) {
  return (
    <div className="space-y-3">
      <Field label="Name"><Input data-testid="person-name" value={form.name || ""} onChange={(e) => set({ name: e.target.value })}/></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email"><Input data-testid="person-email" type="email" value={form.email || ""} onChange={(e) => set({ email: e.target.value })}/></Field>
        <Field label="Phone"><Input data-testid="person-phone" value={form.phone || ""} onChange={(e) => set({ phone: e.target.value })}/></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Location"><Input value={form.location || ""} onChange={(e) => set({ location: e.target.value })}/></Field>
        <Field label="Website"><Input value={form.website || ""} onChange={(e) => set({ website: e.target.value })}/></Field>
      </div>
      <Field label="Role tags">
        <div className="flex flex-wrap gap-1.5">
          {ROLE_OPTIONS.map((t) => (
            <button key={t} type="button" onClick={() => toggleRole(t)}
              data-testid={`person-role-${t.replace(/\s+/g, "-")}`}
              className={`text-xs px-2 py-1 rounded-md border ${form.role_tags?.includes(t) ? "bg-[hsl(var(--studio-primary))] text-white border-transparent" : "border-border text-muted-foreground hover:bg-accent"}`}>
              {t}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Notes"><Textarea rows={2} value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })}/></Field>
      <ImageUploadField label="Headshot / profile photo" round cropAspect={1} testId="person-photo"
                        hint="JPG, PNG or WebP up to 5 MB — you'll frame it square before it saves."
                        value={form.photo_url} onChange={(v) => set({ photo_url: v })}
                        context={form.id ? { person_id: form.id } : {}} />
    </div>
  );
}
